import { OpenPanel } from "@openpanel/web";

// OpenPanel analytics, matching how apps/frontend does it.
//
// Two mistakes have silently produced zero events on this estate before, both
// worth not repeating: a bare side-effect import gets tree-shaken because
// nothing consumes its exports (so `initAnalytics()` is called explicitly from
// main.tsx), and any CSP in front of the app must allow connecting to
// openpanel-api.gazar.dev or the SDK's fetch is refused.

const isLocalhost =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

// Headless crawlers execute JavaScript, so the SDK counts them unless they are
// filtered here. Copied deliberately from apps/frontend/src/utils/analytics.ts:
// the numbers from these two apps get compared, so they must be filtered the
// same way or the comparison is meaningless.
function isLikelyBot(): boolean {
  if (typeof navigator === "undefined") return true;
  const nav = navigator as Navigator & { webdriver?: boolean };
  if (nav.webdriver) return true;
  const ua = nav.userAgent || "";
  if (!ua) return true;
  if (
    /HeadlessChrome|PhantomJS|Selenium|puppeteer|playwright|Electron|bot|crawler|spider|scrapy|python-requests|Go-http-client|MSIE [6-9]\./i.test(
      ua,
    )
  ) {
    return true;
  }
  if (Array.isArray(nav.languages) && nav.languages.length === 0) return true;
  return false;
}

const clientId = import.meta.env.VITE_PUBLIC_OPENPANEL_CLIENT_ID as string | undefined;
const apiUrl =
  (import.meta.env.VITE_PUBLIC_OPENPANEL_API_URL as string | undefined) ??
  "https://openpanel-api.gazar.dev";

let op: OpenPanel | null = null;

export function initAnalytics() {
  if (op || !clientId || isLocalhost || isLikelyBot()) return op;
  op = new OpenPanel({
    apiUrl,
    clientId,
    trackScreenViews: true,
    trackOutgoingLinks: false,
    trackAttributes: false,
  });
  return op;
}

type Props = Record<string, unknown>;

/**
 * The events worth having.
 *
 * This is a single-user practice tool, so the question analytics answers here is
 * not "how many visitors" but "am I actually using it, and which patterns keep
 * failing". Hence one event per graded attempt carrying the pattern and grade,
 * which is the only series that answers that.
 */
export const analytics = {
  identify(profileId: string) {
    op?.identify({ profileId });
  },

  drillServed(props: { drillId: string; pattern: string; reason: string }) {
    op?.track("drill_served", props);
  },

  drillRun(props: { drillId: string; pattern: string; run: number; passed: boolean }) {
    op?.track("drill_run", props);
  },

  drillGraded(props: {
    drillId: string;
    pattern: string;
    grade: number;
    score: number;
    elapsedMs: number;
    runs: number;
    revealed: boolean;
    ahead: boolean;
    hintsUsed: number;
  }) {
    op?.track("drill_graded", props);
  },

  // Which rung, on which pattern, and how far into the budget. This is the
  // series that says where the bank is teaching badly rather than where the
  // person is weak: a drill everyone needs rung two on has a statement problem.
  drillHint(props: { drillId: string; pattern: string; rung: number; elapsedMs: number }) {
    op?.track("drill_hint", props);
  },

  pasteBlocked(props: { drillId: string }) {
    op?.track("paste_blocked", props);
  },

  capture(event: string, properties?: Props) {
    op?.track(event, properties);
  },
};
